import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "backdrop", "modal", "searchInput", "results", "quickActions", "navigationItems", "helpItems", "actionItem", "searchResults", "searchResultsList", "noResults", "quickActionsSection", "navigationSection", "helpSection", "loading"]
  static values = { 
    allowBackgroundClose: Boolean, 
    closeOnEsc: Boolean 
  }

  connect() {
    this.isOpen = false
    this.selectedIndex = -1
    this.filteredItems = []
    this.searchTimeout = null
    
    // Bind the global keydown handler to preserve context
    this.boundHandleGlobalKeydown = this.handleGlobalKeydown.bind(this)
    
    // Keyboard shortcut listener
    document.addEventListener('keydown', this.boundHandleGlobalKeydown)
    
    // Listen for quick access events
    this.boundHandleQuickAccessOpen = this.handleQuickAccessOpen.bind(this)
    this.element.addEventListener('quick-access:open', this.boundHandleQuickAccessOpen)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleGlobalKeydown)
    this.element.removeEventListener('quick-access:open', this.boundHandleQuickAccessOpen)
  }

  handleQuickAccessOpen() {
    this.open()
  }


  handleGlobalKeydown(event) {
    // Cmd+K (Mac) / Ctrl+K (Linux/Windows) zum Oeffnen — Industry-Standard
    // (Linear, Vercel, Stripe, Notion, GitHub). ALT+Q war kollidiert mit
    // macOS-DE-Layout (Option+Q = "œ") und ist unintuitiv.
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault()
      this.open()
    }

    // ESC to close when open
    if (this.isOpen && event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      this.close()
    }
  }

  open() {
    if (this.isOpen) return
    
    this.isOpen = true
    this.containerTarget.classList.remove('hidden')
    this.searchInputTarget.value = ''
    this.search('')
    
    // Add body scroll lock
    document.body.style.overflow = 'hidden'
    
    // Animate modal in
    requestAnimationFrame(() => {
      this.modalTarget.style.transform = 'scale(0.95)'
      this.modalTarget.style.opacity = '0'
      this.backdropTarget.style.opacity = '0'
      
      requestAnimationFrame(() => {
        this.modalTarget.style.transform = 'scale(1)'
        this.modalTarget.style.opacity = '1'
        this.backdropTarget.style.opacity = '1'
        
        // Focus and select all text in search input after animation
        this.searchInputTarget.focus()
        this.searchInputTarget.select()
      })
    })
  }

  close() {
    if (!this.isOpen) return
    
    this.isOpen = false
    this.selectedIndex = -1
    
    // Animate modal out
    this.modalTarget.style.transform = 'scale(0.95)'
    this.modalTarget.style.opacity = '0'
    this.backdropTarget.style.opacity = '0'
    
    setTimeout(() => {
      this.containerTarget.classList.add('hidden')
      // Remove body scroll lock
      document.body.style.overflow = ''
      
      // Reset search
      this.searchInputTarget.value = ''
      this.search('')
      
      // Remove focus from search input
      this.searchInputTarget.blur()
      
      // Dispatch custom event for other controllers
      document.dispatchEvent(new CustomEvent('command-palette:closed'))
    }, 150)
  }

  closeBackground(event) {
    if (this.allowBackgroundCloseValue && event.target === this.containerTarget) {
      this.close()
    }
  }


  search(event) {
    const query = event ? event.target.value.toLowerCase() : ''
    this.selectedIndex = -1
    
    // Clear previous search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    if (query === '') {
      this.showAllSections()
      this.hideSearchResults()
      return
    }
    
    // Search in static sections
    this.hideAllSections()
    this.filteredItems = []
    
    // Search in quick actions
    this.searchInSection(this.quickActionsTarget, query, 'quickActions')
    
    // Search in navigation
    this.searchInSection(this.navigationItemsTarget, query, 'navigation')
    
    // Search in help
    this.searchInSection(this.helpItemsTarget, query, 'help')

    // Perform backend search with debounce (300ms delay)
    this.searchTimeout = setTimeout(() => {
      this.performBackendSearch(query)
    }, 300)

    // Show results container
    this.resultsTarget.classList.remove('hidden')

    // Erstes Item auswaehlen — sofortiges Enter trifft den naheliegendsten
    // Treffer in Quick-Actions/Navigation; Backend-Search-Treffer ueberschreiben
    // diese Auswahl spaeter wenn vorhanden (siehe displaySearchResults).
    if (this.filteredItems.length > 0) {
      this.selectedIndex = 0
      this.updateSelection()
    }
  }

  searchInSection(section, query, sectionName) {
    if (!section) return
    
    const items = section.querySelectorAll('[data-command-palette-target="actionItem"]')
    let hasMatches = false
    
    items.forEach((item, index) => {
      const title = item.querySelector('.text-sm.font-medium')?.textContent.toLowerCase() || ''
      const description = item.querySelector('.text-xs.text-gray-500')?.textContent.toLowerCase() || ''
      
      if (title.includes(query) || description.includes(query)) {
        item.classList.remove('hidden')
        this.filteredItems.push({
          element: item,
          section: sectionName,
          index: this.filteredItems.length
        })
        hasMatches = true
      } else {
        item.classList.add('hidden')
      }
    })
    
    // Show section header if there are matches
    if (hasMatches) {
      const sectionHeader = section.previousElementSibling
      if (sectionHeader) {
        sectionHeader.classList.remove('hidden')
        section.classList.remove('hidden')
      }
    }
  }

  showAllSections() {
    // Hide search results + loading when showing all sections
    this.hideSearchResults()
    this.hideLoading()

    // filteredItems neu aufbauen, damit Pfeiltasten + Enter direkt nach dem
    // Oeffnen (ohne Tippen) funktionieren.
    this.filteredItems = []
    const sectionTargets = [
      { target: this.quickActionsSectionTarget,  name: 'quickActions' },
      { target: this.navigationSectionTarget,    name: 'navigation' },
      { target: this.helpSectionTarget,          name: 'help' }
    ]
    sectionTargets.forEach(({ target, name }) => {
      if (!target) return
      target.classList.remove('hidden')
      const items = target.querySelectorAll('[data-command-palette-target="actionItem"]')
      items.forEach(item => {
        item.classList.remove('hidden')
        this.filteredItems.push({ element: item, section: name, index: this.filteredItems.length })
      })
    })

    this.resultsTarget.classList.remove('hidden')

    // Default-Selection: erstes Item — Enter direkt nach Open trifft Item 0
    // (Standard-Verhalten Linear/Vercel/Notion).
    if (this.filteredItems.length > 0) {
      this.selectedIndex = 0
      this.updateSelection()
    }
  }

  hideAllSections() {
    const sectionTargets = [this.quickActionsSectionTarget, this.navigationSectionTarget, this.helpSectionTarget]
    sectionTargets.forEach(section => {
      if (section) {
        section.classList.add('hidden')
      }
    })
  }

  handleKeydown(event) {
    if (!this.isOpen) return
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.navigateDown()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.navigateUp()
        break
      case 'Enter':
        event.preventDefault()
        this.selectCurrentItem()
        break
      case 'Escape':
        event.preventDefault()
        this.close()
        break
    }
  }

  navigateDown() {
    if (this.filteredItems.length === 0) return
    
    this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredItems.length - 1)
    this.updateSelection()
  }

  navigateUp() {
    if (this.filteredItems.length === 0) return
    
    this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
    this.updateSelection()
  }

  updateSelection() {
    // Remove previous selection
    this.filteredItems.forEach(item => {
      item.element.classList.remove('bg-blue-50', 'text-blue-700')
      item.element.classList.add('hover:bg-gray-50', 'hover:text-gray-700')
    })
    
    // Add selection to current item
    if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredItems.length) {
      const currentItem = this.filteredItems[this.selectedIndex]
      currentItem.element.classList.add('bg-blue-50', 'text-blue-700')
      currentItem.element.classList.remove('hover:bg-gray-50', 'hover:text-gray-700')
      
      // Scroll into view
      currentItem.element.scrollIntoView({ block: 'nearest' })
    }
  }

  selectCurrentItem() {
    if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredItems.length) {
      const currentItem = this.filteredItems[this.selectedIndex]
      // Backend-Search-Items SIND ein <a>, statische Section-Items KOENNEN ein <a> sein.
      // Bevorzugt: das Element selbst pruefen, sonst child <a>.
      const link = currentItem.element.tagName === 'A'
        ? currentItem.element
        : currentItem.element.querySelector('a')
      if (link && link.href) {
        // Erst Modal schliessen, sonst bleibt overflow:hidden auf body kleben
        // wenn die Folgeseite via Turbo geladen wird.
        this.close()
        // .click() respektiert Turbo + target=_blank, window.location.href nicht.
        link.click()
      } else {
        currentItem.element.click()
      }
    }
  }

  async performBackendSearch(query) {
    if (!query || query.length < 2) {
      this.hideSearchResults()
      this.hideLoading()
      return
    }

    this.showLoading()

    try {
      // Search in applicants, talents, contacts and customers
      // (Pernkopf 2026-08-20: Kunden fehlten in der Backend-Suche.)
      // Pernkopf 2026-09-03: allSettled statt all — sonst killt ein einzelner
      // 500er (z.B. contacts) alle anderen Treffer und die Palette bleibt leer.
      const settled = await Promise.allSettled([
        this.searchModel('applicants', query),
        this.searchModel('talents', query),
        this.searchModel('contacts', query),
        this.searchModel('customers', query)
      ])
      const [applicants, talents, contacts, customers] = settled.map((r, i) => {
        if (r.status === 'fulfilled') return r.value
        console.error(`Search failed for ${['applicants','talents','contacts','customers'][i]}:`, r.reason)
        return []
      })

      this.displaySearchResults(applicants, talents, contacts, customers, query)
    } catch (error) {
      console.error('Search error:', error)
      this.hideSearchResults()
    } finally {
      this.hideLoading()
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove('hidden')
  }

  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add('hidden')
  }

  async searchModel(model, term) {
    const response = await fetch(`/backend/search?model=${model}&term=${encodeURIComponent(term)}`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    
    if (!response.ok) {
      throw new Error(`Search failed for ${model}`)
    }
    
    return response.json()
  }

  displaySearchResults(applicants, talents, contacts, customers, query) {
    const hasResults = applicants.length > 0 || talents.length > 0 || contacts.length > 0 || customers.length > 0

    if (!hasResults) {
      this.showNoResults()
      return
    }

    // Alte Backend-Search-Eintraege aus filteredItems entfernen, sonst
    // wuerden Pfeiltasten durch DOM-fremde Geister-Items navigieren.
    this.filteredItems = this.filteredItems.filter(i => i.section !== 'searchResults')

    // Clear previous results
    this.searchResultsListTarget.innerHTML = ''

    // Index merken, ab dem die neuen Backend-Treffer beginnen — Auswahl
    // springt nach Fetch direkt auf den ersten Backend-Treffer (relevanter
    // als statische Quick-Actions wenn der Nutzer gerade getippt hat).
    const backendStartIndex = this.filteredItems.length

    // Add applicants — Treffer ohne id ueberspringen (AppSignal #344:
    // verhindert URL /backend/applicants/null aus partiellen Backend-Treffern).
    applicants.forEach(applicant => {
      if (!applicant || applicant.id == null) return
      const item = this.createSearchResultItem(
        applicant,
        `/backend/applicants/${applicant.id}`,
        'user',
        'blue',
        'Bewerber'
      )
      this.searchResultsListTarget.appendChild(item)
      this.filteredItems.push({ element: item, section: 'searchResults', index: this.filteredItems.length })
    })

    // Add talents
    talents.forEach(talent => {
      if (!talent || talent.id == null) return
      const item = this.createSearchResultItem(
        talent,
        `/backend/talents/${talent.id}`,
        'star',
        'green',
        'Talent'
      )
      this.searchResultsListTarget.appendChild(item)
      this.filteredItems.push({ element: item, section: 'searchResults', index: this.filteredItems.length })
    })

    // Add contacts
    contacts.forEach(contact => {
      if (!contact || contact.id == null) return
      const item = this.createSearchResultItem(
        contact,
        `/backend/contacts/${contact.id}`,
        'address-book',
        'purple',
        'Kontakt'
      )
      this.searchResultsListTarget.appendChild(item)
      this.filteredItems.push({ element: item, section: 'searchResults', index: this.filteredItems.length })
    })

    // Add customers (Pernkopf 2026-08-20)
    customers.forEach(customer => {
      if (!customer || customer.id == null) return
      const item = this.createSearchResultItem(
        customer,
        `/backend/customers/${customer.id}`,
        'building',
        'orange',
        'Kunde'
      )
      this.searchResultsListTarget.appendChild(item)
      this.filteredItems.push({ element: item, section: 'searchResults', index: this.filteredItems.length })
    })

    this.showSearchResults()

    // Auswahl auf ersten Backend-Treffer springen — nach dem Tippen ist das
    // der relevanteste Eintrag, nicht eine zufaellig matchende Quick-Action.
    if (this.filteredItems.length > backendStartIndex) {
      this.selectedIndex = backendStartIndex
      this.updateSelection()
    }
  }

  createSearchResultItem(record, url, icon, color, type) {
    const item = document.createElement('a')
    item.href = url
    item.className = `group flex items-center px-3 py-2 rounded-lg hover:bg-${color}-50 hover:text-${color}-700 transition-colors duration-150 cursor-pointer`
    item.setAttribute('data-command-palette-target', 'actionItem')

    const colorClasses = {
      blue: 'text-blue-400 group-hover:text-blue-500',
      green: 'text-green-400 group-hover:text-green-500',
      purple: 'text-purple-400 group-hover:text-purple-500',
      orange: 'text-orange-400 group-hover:text-orange-500'
    }

    item.innerHTML = `
      <div class="flex-shrink-0 mr-3">
        <i class="fas fa-${icon} h-5 w-5 ${colorClasses[color]}"></i>
      </div>
      <div class="flex-1 min-w-0">
        <div class="text-sm font-medium truncate">${this.escapeHtml(record.text)}</div>
        <div class="text-xs text-gray-500 truncate">${record.subtext || ''}</div>
      </div>
      <div class="flex-shrink-0 ml-2">
        <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${color}-100 text-${color}-800">
          ${type}
        </span>
      </div>
    `

    return item
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  showSearchResults() {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.remove('hidden')
    }
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.add('hidden')
    }
  }

  hideSearchResults() {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.add('hidden')
    }
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.add('hidden')
    }
  }

  showNoResults() {
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.remove('hidden')
    }
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.remove('hidden')
    }
    this.searchResultsListTarget.innerHTML = ''
  }
}
